import axios from 'axios';
import { useStore } from 'vuex';
import { useAuth0 } from '@auth0/auth0-vue';
import { jwtDecode } from "jwt-decode";

function useRequest(baseUrl) {
	const store = useStore();
	const { getAccessTokenSilently, user, logout } = useAuth0();

	const handleLogout = () =>
		logout({
			logoutParams: {
				returnTo: process.env.VUE_APP_BASE_URL,
			}
		});

	const makeRequest = async (endpoint, method, data = {}, params = {}) => {
		try {
			const token = await getAccessTokenSilently();

			if (!store.state.authentication.userIsAuthorized) {
				const decoded = jwtDecode(token);
				store.commit('SET_AUTHENTICATION', {
					...store.state.authentication,
					userIsAuthorized: true,
					roles: user.value['https://structure.io/roles'],
					permissions: decoded.permissions,
				});
			}

			const response = await axios({
				url: baseUrl + endpoint,
				method,
				data,
				params,
				headers: { Authorization: 'Bearer ' + token },
			});
			return response.data;
		} catch (error) {
			if (error.response && error.response.status === 401) {
				handleLogout();
			} else {
				error.message ? store.commit('SET_ERROR_MESSAGE', error.message) : store.commit('SET_ERROR_MESSAGE', 'An error occurred');
				store.commit('SET_SHOW_ERROR_MODAL', true);
				store.commit('SET_SHOW_LOADING_MODAL', false);
			}
		}
	};

	return { makeRequest };
}

export function useSCPRequest() {
	const { makeRequest } = useRequest(process.env.VUE_APP_SCP_BASE_URL);
	return { makeSCPRequest: makeRequest };
}

export function useCBERequest() {
	const { makeRequest } = useRequest(process.env.VUE_APP_CBE_BASE_URL);
	return { makeCBERequest: makeRequest };
}

export const DOCUMENTATION_URL = 'https://docs.structure.io/ios-sdk/documentation/structure/';

export const MOBILE_BREAKPOINT = 912;

export function useDocumentationTracking() {
	const { isAuthenticated, user } = useAuth0();
	const { makeCBERequest } = useCBERequest();

	const handleDocumentationClick = async () => {
		if (!isAuthenticated.value) return false;

		// makeCBERequest swallows request errors internally and resolves to
		// undefined rather than rejecting, so success must be judged by its
		// return value, not by catching a thrown error.
		const response = await makeCBERequest(`/piccolo/users/onboardings/${user.value.sub}`, 'patch', { has_read_documentation: true });
		return response !== undefined;
	};

	return { documentationUrl: DOCUMENTATION_URL, handleDocumentationClick };
}
